import { Box, alpha, useTheme } from '@mui/material';
import { useEffect, useRef, useState, useCallback } from 'react';
import TemplateVarSwitcher from './TemplateVarSwitcher';
import RenderHTML from '../preview/RenderHTML';
import ExoTabs from '../exo/ExoTabs';
import StyledTextContainer from '../preview/StyledTextContainer';
import MailPreviewer from '../preview/MailPreviewer';
import { AttachmentPreviewer } from '../preview/AttachmentPreviewer';
import { tokens } from '../../global/theme/tokens';

// A4 dimensions: 210mm x 297mm, aspect ratio = 210/297 ≈ 0.7071
const A4_ASPECT_RATIO = 210 / 297;
const DEFAULT_PAGE_WIDTH = 794; // Default width in pixels (approx A4 at 96dpi)

// Any whitelisted paper size, optionally with an orientation ("A4", "A4 landscape",
// "letter-portrait"). Mirrors the backend's PdfPaperFormat whitelist.
const PAPER_FORMAT_REGEX = /^(a[345]|letter|legal)([\s_-]+(landscape|portrait))?$/i;

export function isPaperFormat(templateFormat) {
  return PAPER_FORMAT_REGEX.test((templateFormat || '').trim());
}

export function isLandscapeFormat(templateFormat) {
  return (
    isPaperFormat(templateFormat) && /landscape/i.test(templateFormat || '')
  );
}

const TemplatePreview = ({
  data,
  edit = false,
  className = '',
  onSelect = () => {},
  paginate = true,
  scaleable = false,
  background = false,
  onEdit = () => {},
  onChange = () => {},
  fit = false,
  sx,
  disableVariableRendering,
  blank,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const containerRef = useRef(null);
  const [template, setTemplate] = useState({});
  const [scale, setScale] = useState(1);

  // Get page width from template styles. A landscape sheet is printed with the
  // paper's long edge as its width, so the configured (portrait) canvas size
  // becomes the height and the width grows by the aspect ratio — mirroring how
  // Chromium lays out the page for the PDF.
  const getPageWidth = useCallback(() => {
    let size = DEFAULT_PAGE_WIDTH;
    if (data?.styleJson) {
      try {
        size = JSON.parse(data.styleJson).page?.size || DEFAULT_PAGE_WIDTH;
      } catch {
        size = DEFAULT_PAGE_WIDTH;
      }
    }
    return isLandscapeFormat(data?.templateFormat)
      ? size / A4_ASPECT_RATIO
      : size;
  }, [data?.styleJson, data?.templateFormat]);

  // Calculate scale to fit container width exactly
  const calculateScale = useCallback(
    (containerWidth) => {
      if (!fit || containerWidth === 0) {
        return 1;
      }
      const pageWidth = getPageWidth();
      return paginate
        ? containerWidth / (pageWidth * 1.027)
        : containerWidth / pageWidth;
    },
    [fit, getPageWidth]
  );

  // Handle resize with ResizeObserver
  useEffect(() => {
    if (blank || !containerRef.current) return;

    // Initial calculation
    const initialWidth = containerRef.current.getBoundingClientRect().width;
    if (initialWidth > 0) {
      setScale(calculateScale(initialWidth));
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) {
          setScale(calculateScale(width));
        }
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [blank, calculateScale]);

  // Update template when data changes
  useEffect(() => {
    if (blank || !data) return;
    setTemplate(data);
  }, [data, blank]);

  if (blank) {
    return (
      <Box
        className="w-full flex items-center justify-center rounded-lg"
        sx={{
          aspectRatio: A4_ASPECT_RATIO,
          bgcolor: alpha(colors.card, 0.5),
          border: `2px dashed ${alpha(colors.card, 0.3)}`,
        }}
      >
        <span style={{ opacity: 0.5, fontSize: '14px' }}>TEMPLATE</span>
      </Box>
    );
  }

  const pageWidth = getPageWidth();
  const pageHeight = isLandscapeFormat(template.templateFormat)
    ? pageWidth * A4_ASPECT_RATIO
    : pageWidth / A4_ASPECT_RATIO;

  // StyledTextContainer reads the sheet width from styleJson, so the preview
  // copy of a landscape template needs its page size widened accordingly.
  let previewTemplate = template;
  if (isLandscapeFormat(template.templateFormat) && template.styleJson) {
    try {
      const styles = JSON.parse(template.styleJson);
      styles.page = { ...styles.page, size: pageWidth };
      previewTemplate = { ...template, styleJson: JSON.stringify(styles) };
    } catch {
      // keep the original styleJson if it is not valid JSON
    }
  }

  return (
    <Box
      ref={containerRef}
      className={'w-full ' + className}
      sx={{
        ...sx,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Preview container - scales to fit width */}
      <Box
        sx={{
          width: '100%',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        {template.templateFormat === 'Mail' && (
          <MailPreviewer
            template={template}
            onEdit={onEdit}
            scale={scale}
            disableVariableRendering={disableVariableRendering}
          />
        )}
        {template.templateFormat === 'Attachment' && (
          <AttachmentPreviewer
            edit={edit}
            template={template}
            scale={scale}
            onSelect={onSelect}
            paginate={paginate}
            scaleable={scaleable}
            background={background}
            onClick={onEdit}
            onChange={onChange}
            disableVariableRendering={disableVariableRendering}
          />
        )}
        {isPaperFormat(template.templateFormat) && (
          <A4Preview
            edit={edit}
            template={previewTemplate}
            scale={scale}
            onSelect={onSelect}
            paginate={paginate}
            scaleable={scaleable}
            background={background}
            onClick={onEdit}
            onChange={onChange}
            disableVariableRendering={disableVariableRendering}
            pageWidth={pageWidth}
            pageHeight={pageHeight}
          />
        )}
        {template.templateFormat === 'Web' && (
          <RenderPage
            page={1}
            edit={edit}
            template={template}
            scale={scale}
            onSelect={onSelect}
            scaleable={scaleable}
            background={background}
            onEdit={onEdit}
            disableVariableRendering={disableVariableRendering}
            pageWidth={pageWidth}
            pageHeight={pageHeight}
          />
        )}
      </Box>
    </Box>
  );
};

const A4Preview = ({
  template,
  scale,
  onSelect,
  edit,
  scaleable,
  background,
  onClick,
  onChange,
  paginate,
  disableVariableRendering,
  pageWidth,
  pageHeight,
}) => {
  // StyledTextContainer sizes the sheet as pageWidth / aspectRatio, so a
  // landscape page needs the inverted ratio — not the A4 portrait constant.
  const aspectRatio = pageWidth / pageHeight;

  if (!paginate) {
    return (
      <RenderPage
        page={1}
        edit={edit}
        template={template}
        scale={scale}
        onSelect={onSelect}
        scaleable={scaleable}
        background={background}
        aspectRatio={aspectRatio}
        onClick={onClick}
        onChange={onChange}
        disableVariableRendering={disableVariableRendering}
        pageWidth={pageWidth}
        pageHeight={pageHeight}
      />
    );
  }

  const tabs = [
    {
      label: 'Page 1',
      content: (
        <RenderPage
          page={1}
          edit={edit}
          template={template}
          scale={scale}
          onSelect={onSelect}
          scaleable={scaleable}
          background={background}
          aspectRatio={aspectRatio}
          onClick={onClick}
          onChange={onChange}
          disableVariableRendering={disableVariableRendering}
          pageWidth={pageWidth}
          pageHeight={pageHeight}
        />
      ),
    },
    {
      label: 'Page 2',
      content: (
        <RenderPage
          page={2}
          edit={edit}
          template={template}
          scale={scale}
          onSelect={onSelect}
          scaleable={scaleable}
          background={background}
          aspectRatio={aspectRatio}
          onClick={onClick}
          onChange={onChange}
          disableVariableRendering={disableVariableRendering}
          pageWidth={pageWidth}
          pageHeight={pageHeight}
        />
      ),
    },
  ];

  return <ExoTabs tabs={tabs} />;
};

const RenderPage = ({
  page,
  template,
  onSelect,
  edit,
  background,
  scaleable,
  aspectRatio,
  onClick,
  scale,
  onChange = () => {},
  disableVariableRendering,
  pageWidth,
  pageHeight,
}) => {
  const [height, setHeight] = useState({ header: 0, content: 0, footer: 0 });

  const headerRef = useRef(null);
  const footerRef = useRef(null);

  const zIndex = 10;

  useEffect(() => {
    if (!template.styleJson) return;
    const styles = JSON.parse(template.styleJson);
    handleChange(page, styles);
  }, [template, page]);

  function handleChange(page, styles) {
    let headerHeight = headerRef.current?.clientHeight || 0;
    let footerHeight = footerRef.current?.clientHeight || 0;

    // Check if the height has changed
    if (
      styles.page['headerP' + page + 'Height'] === headerHeight &&
      styles.page['footerP' + page + 'Height'] === footerHeight
    ) {
      return;
    }

    // Get height of hidden page
    if (headerHeight === 0 && footerHeight === 0) {
      headerHeight = getHeightOfHiddenContainer(headerRef);
      footerHeight = getHeightOfHiddenContainer(footerRef);
    }

    // Calculate content height
    const contentHeight =
      styles.page.size / aspectRatio -
      styles.papermargin.top -
      styles.papermargin.bottom -
      headerHeight -
      footerHeight;

    setHeight({
      header: headerHeight,
      content: styles.page?.allowBottomOverflow
        ? contentHeight + footerHeight
        : contentHeight,
      footer: footerHeight,
    });

    onChange(page, headerHeight, footerHeight);
  }

  function getHeightOfHiddenContainer(containerRef) {
    if (!containerRef.current) return 0;

    // Clone the hidden container
    const clonedContainer = containerRef.current.cloneNode(true);

    // Set the cloned container to be hidden
    clonedContainer.style.visibility = 'hidden';
    clonedContainer.style.position = 'absolute';
    clonedContainer.style.height = 'auto';

    // Append the cloned container to the DOM
    document.body.appendChild(clonedContainer);

    // Get the height of the cloned container
    const containerHeight = clonedContainer.clientHeight;

    // Remove the cloned container from the DOM
    document.body.removeChild(clonedContainer);

    return containerHeight;
  }

  const hoverHighlightClass =
    'hover:bg-green-100 hover:cursor-pointer transition-colors';

  return (
    <StyledTextContainer
      presetScale={scale}
      sx={{ zIndex: zIndex }}
      styleJson={template.styleJson}
      scaleable={scaleable}
      background={background}
      aspectRatio={aspectRatio}
      onClick={onClick}
    >
      <RenderHTML
        innerRef={headerRef}
        onClick={() => onSelect('headerP' + page)}
        className={'w-full ' + (edit && hoverHighlightClass)}
        HTML={
          disableVariableRendering
            ? template['headerP' + page]
            : TemplateVarSwitcher(template['headerP' + page], template.vars)
        }
      />
      <RenderHTML
        onClick={() => onSelect('content')}
        className={'w-full ' + (edit && hoverHighlightClass)}
        sx={{
          top: height.header + 'px',
          height: height.content + 'px',
          overflowY: 'auto!important',
          '::-webkit-scrollbar': {
            display: 'block',
            width: '0px',
          },
        }}
        HTML={
          disableVariableRendering
            ? template.content
            : TemplateVarSwitcher(template.content, template.vars)
        }
      />
      <RenderHTML
        innerRef={footerRef}
        onClick={() => onSelect('footerP' + page)}
        className={'absolute bottom-0 w-full ' + (edit && hoverHighlightClass)}
        HTML={
          disableVariableRendering
            ? template['footerP' + page]
            : TemplateVarSwitcher(template['footerP' + page], template.vars)
        }
      />
    </StyledTextContainer>
  );
};

export default TemplatePreview;
